import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOption, MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { map } from 'rxjs';

@Component({
  selector: 'app-improved-select',
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatChipsModule,
    ScrollingModule,
    MatTooltipModule,
  ],
  templateUrl: './improved-select.component.html',
  styleUrl: './improved-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImprovedSelectComponent<T> implements ControlValueAccessor, OnInit {
  viewport = viewChild<CdkVirtualScrollViewport>('viewport');
  virtualOptions = viewChildren<MatOption>('virtualOption');

  private readonly cdr = inject(ChangeDetectorRef);
  public ngControl = inject(NgControl, {
    optional: true,
    self: true,
  });

  label = input<string>();
  showChips = input<boolean>(false);
  showSelectAll = input<boolean>(false);
  optionList = input.required<T[]>();
  multiple = input<boolean>(false);
  valueKey = input<keyof T>();
  displayKey = input<keyof T>();
  unselect = input<boolean>(false);
  loading = input<boolean>(false);
  errorMessages = input<Record<string, string>>({});

  selectedControl = new FormControl([] as T[], { nonNullable: true });
  selectedList = toSignal(this.selectedControl.valueChanges.pipe(map(value => (Array.isArray(value) ? value : [value]))), {
    initialValue: [] as T[],
  });
  hiddenOptions = computed(() => {
    const vKey = this.valueKey();
    if (vKey) {
      // Use valueKey for comparison instead of reference equality
      const virtualOptionKeys = new Set(this.virtualOptions().map(matOption => matOption.value?.[vKey]));
      return this.selectedList().filter(value => !virtualOptionKeys.has(value[vKey]));
    }
    // Fallback to reference equality when no valueKey is provided
    const virtualOptions = new Set(this.virtualOptions().map(matOption => matOption.value));
    return this.selectedList().filter(value => !virtualOptions.has(value));
  });

  allFilteredSelected = computed(() => {
    const selectedFilteredCount = this.selectedFilteredCount();
    const filteredCount = this.filteredList().length;

    return selectedFilteredCount > 0 && selectedFilteredCount === filteredCount;
  });

  someFilteredSelected = computed(() => {
    const selectedFilteredCount = this.selectedFilteredCount();
    const filteredCount = this.filteredList().length;

    return selectedFilteredCount > 0 && selectedFilteredCount < filteredCount;
  });

  private readonly selectedFilteredCount = computed(() => {
    const filtered = this.filteredList();
    if (filtered.length === 0) return 0;

    const selected = this.selectedList();
    const vKey = this.valueKey();

    if (vKey) {
      const selectedKeys = new Set(selected.map(item => item[vKey]));
      return filtered.filter(item => selectedKeys.has(item[vKey])).length;
    }

    const selectedSet = new Set(selected);
    return filtered.filter(item => selectedSet.has(item)).length;
  });

  value = signal('');

  filteredList = computed(() => {
    const query = this.value().toLowerCase();

    const filterValue = this.displayKey() ? (option: T) => String(option[this.displayKey()!]) : (option: T) => String(option);
    return this.optionList().filter(option => filterValue(option).toLowerCase().includes(query));
  });

  readonly itemSize = 48;
  readonly maxHeight = 185; // fallback when items are more than the view

  viewportHeight = computed(() => {
    const itemCount = this.unselect() ? this.filteredList().length + 1 : this.filteredList().length;
    const dynamicHeight = itemCount * this.itemSize || this.itemSize;
    return Math.min(dynamicHeight, this.maxHeight);
  });

  private changedFromWrite = false;
  private onChange: (value: T | T[]) => void = () => {
    // Initialized as no-op; will be assigned by Angular forms via registerOnChange.
  };
  private onTouched: () => void = () => {
    // Initialized as no-op; will be assigned by Angular forms via registerOnTouched.
  };

  constructor() {
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      this.onTouched();
      const selectedList = this.selectedList();
      if (!this.changedFromWrite) {
        this.onChange(this.multiple() ? selectedList : selectedList[0]);
      } else {
        this.changedFromWrite = false;
      }

      // mark for check to be sure everything is rendered correctly
      // errors outside component weren't rerendered on time
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    // pass validators from parent to selectedControl
    // not working for dynamic validators
    if (this.ngControl?.control) {
      this.selectedControl.setValidators(this.ngControl.control.validator);
    }
  }

  onOpenChange(opened: boolean) {
    // reset filter and scroll position
    if (!opened) {
      this.value.set('');
      this.selectedControl.markAsTouched();
      this.cdr.markForCheck();
    }
    if (!opened && this.viewport()) {
      this.viewport()?.scrollToIndex(0);
      this.viewport()?.checkViewportSize();
    }
  }

  removeChip(value: T) {
    const updatedList = this.selectedList().filter(item =>
      this.valueKey() ? item[this.valueKey()!] !== value[this.valueKey()!] : item !== value
    );
    this.selectedControl.setValue(updatedList);

    this.selectedControl.markAsTouched();
    this.selectedControl.updateValueAndValidity();
  }

  compareWith = (a: T, b?: T | T[]): boolean => {
    // selectedControl is always an array, so we need to handle both cases
    const bValue = Array.isArray(b) ? b[0] : b;
    return this.valueKey() ? a[this.valueKey()!] === bValue?.[this.valueKey()!] : a === b;
  };

  writeValue(value: T | T[]): void {
    let arr: T[];
    if (Array.isArray(value)) {
      arr = value;
    } else if (value) {
      arr = [value];
    } else {
      arr = [];
    }
    this.changedFromWrite = true;
    this.selectedControl.setValue(arr);
  }

  registerOnChange(fn: (value: T | T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectedControl.disable({ emitEvent: false });
    } else {
      this.selectedControl.enable({ emitEvent: false });
    }
  }

  toggleSelectAll(select: MatSelect) {
    const filtered = this.filteredList();
    if (this.allFilteredSelected()) {
      // Remove filtered items from selection
      const vKey = this.valueKey();
      if (vKey) {
        const filteredKeys = new Set(filtered.map(item => item[vKey]));
        this.selectedControl.setValue(this.selectedList().filter(item => !filteredKeys.has(item[vKey])));
      } else {
        const filteredSet = new Set(filtered);
        this.selectedControl.setValue(this.selectedList().filter(item => !filteredSet.has(item)));
      }
    } else {
      // Add all filtered items to selection (union with existing)
      const current = this.selectedList();
      const vKey = this.valueKey();
      if (vKey) {
        const currentKeys = new Set(current.map(item => item[vKey]));
        const toAdd = filtered.filter(item => !currentKeys.has(item[vKey]));
        this.selectedControl.setValue([...current, ...toAdd]);
      } else {
        const currentSet = new Set(current);
        const toAdd = filtered.filter(item => !currentSet.has(item));
        this.selectedControl.setValue([...current, ...toAdd]);
      }
    }

    // close panel after selection to correctly update state of select all checkbox
    setTimeout(() => select.close());
  }

  filterErrorMessages(errors: ValidationErrors | null): { key: string; message: string }[] {
    if (!errors) return [];
    return Object.keys(errors)
      .filter(key => !!this.errorMessages()[key])
      .map(key => ({ key, message: this.errorMessages()[key] }));
  }

  clearAll(select: MatSelect) {
    if (this.multiple()) {
      select.close();
      this.selectedControl.setValue([]);
      this.selectedControl.updateValueAndValidity();
    }
  }
}
